import { Button, Dialog, Input, Select } from '@artosera/ui';
import {
  CONFIDENTIALITES,
  TYPES_DOCUMENT,
  type Confidentialite,
  type TypeDocument,
} from '@artosera/shared';
import { UploadCloud } from 'lucide-react';
import type { DragEvent, JSX, KeyboardEvent } from 'react';
import { useRef, useState } from 'react';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { useTranslation } from 'react-i18next';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { useDeposerDocument } from '../../api/coffre.js';
import { Field } from '../Field.js';
import { RechercheRattachement } from './RechercheRattachement.js';
import type { RattachementLu } from './useRattachementResolu.js';
import { FeuilleDocument } from './FeuilleDocument.js';
import styles from './NouveauDocumentDialog.module.css';

export interface NouveauDocumentDialogProps {
  galerieSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDepose: (id: string) => void;
}

function listeVirgule(valeur: string): string[] {
  return valeur
    .split(',')
    .map((entree) => entree.trim())
    .filter((entree) => entree.length > 0);
}

/**
 * « Déposer un document » (3.3, écran 4) : fichier, titre, type,
 * rattachements par recherche, confidentialité, échéance, rappels,
 * étiquettes. La dépose au clavier (D15 : accessibilité) marche par la zone
 * de dépose elle-même — focusable, Entrée/Espace ouvre le sélecteur — en plus
 * du bouton visible, même parti pris que `oeuvres/tabs/MediasTab.tsx`.
 */
export function NouveauDocumentDialog({
  galerieSlug,
  open,
  onOpenChange,
  onDepose,
}: NouveauDocumentDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'coffre', 'commun']);
  const deposer = useDeposerDocument(galerieSlug);
  const fichierRef = useRef<HTMLInputElement>(null);

  const [fichier, setFichier] = useState<File | null>(null);
  const [survole, setSurvole] = useState(false);
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<TypeDocument>('autre');
  const [confidentialite, setConfidentialite] = useState<Confidentialite>('equipe');
  const [echeanceLe, setEcheanceLe] = useState('');
  const [rappelsJours, setRappelsJours] = useState('30,7,1');
  const [etiquettes, setEtiquettes] = useState('');
  const [rattachements, setRattachements] = useState<readonly RattachementLu[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  function choisirFichier(candidat: File | undefined): void {
    if (!candidat) return;
    setFichier(candidat);
    if (titre.trim().length === 0) {
      setTitre(candidat.name.replace(/\.[^.]+$/, ''));
    }
  }

  function surDepot(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setSurvole(false);
    choisirFichier(event.dataTransfer.files[0]);
  }

  function surClavier(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fichierRef.current?.click();
    }
  }

  const garde = useFermetureGardee(
    fichier !== null ||
      titre.trim().length > 0 ||
      etiquettes.trim().length > 0 ||
      rattachements.length > 0,
    onOpenChange,
  );

  async function soumettre(): Promise<void> {
    if (!fichier) return;
    setErreur(null);
    try {
      const reponse = await deposer.mutateAsync({
        fichier,
        titre: titre.trim(),
        type,
        confidentialite,
        ...(echeanceLe ? { echeanceLe } : {}),
        ...(rappelsJours.trim() ? { rappelsJours: listeVirgule(rappelsJours).map(Number) } : {}),
        ...(etiquettes.trim() ? { etiquettes: listeVirgule(etiquettes) } : {}),
        ...(rattachements.length > 0
          ? { rattachements: rattachements.map((r) => `${r.type}:${r.id}`) }
          : {}),
      });
      onOpenChange(false);
      onDepose(reponse.document.id);
    } catch (error) {
      if (estApiError(error)) setErreur(error.message);
      else if (estErreurReseau(error)) setErreur(t('commun:erreurReseau'));
      else setErreur(t('commun:erreurReseau'));
    }
  }

  const peutSoumettre = fichier !== null && titre.trim().length > 0 && !deposer.isPending;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={garde.onOpenChange}
        title={t('regie:coffre.nouveauDocument.titre')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={deposer.isPending}
              disabled={!peutSoumettre}
            >
              {t('regie:coffre.nouveauDocument.deposer')}
            </Button>
          </>
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          <div
            className={styles.zoneDepot}
            data-survole={survole ? 'true' : 'false'}
            data-rempli={fichier ? 'true' : 'false'}
            role="button"
            tabIndex={0}
            aria-label={t('regie:coffre.nouveauDocument.choisirFichier')}
            onClick={() => fichierRef.current?.click()}
            onKeyDown={surClavier}
            onDragOver={(event) => {
              event.preventDefault();
              setSurvole(true);
            }}
            onDragLeave={() => setSurvole(false)}
            onDrop={surDepot}
          >
            {fichier ? (
              <FeuilleDocument nomFichier={fichier.name} />
            ) : (
              <span className={styles.iconeDepot}>
                <UploadCloud size={20} strokeWidth={1.5} aria-hidden />
              </span>
            )}
            <p data-choisi={fichier ? 'oui' : undefined}>
              {fichier ? fichier.name : t('regie:coffre.nouveauDocument.glisserDeposer')}
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                fichierRef.current?.click();
              }}
            >
              {t('regie:coffre.nouveauDocument.choisirFichier')}
            </Button>
            <input
              ref={fichierRef}
              type="file"
              accept="application/pdf,image/*"
              aria-label={t('regie:coffre.nouveauDocument.choisirFichier')}
              hidden
              onChange={(event) => {
                choisirFichier(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </div>

          <Field
            label={t('regie:coffre.nouveauDocument.titreChamp')}
            htmlFor="nouveau-document-titre"
          >
            <Input
              id="nouveau-document-titre"
              required
              value={titre}
              onChange={(event) => setTitre(event.target.value)}
            />
          </Field>

          <div className={styles.grille2}>
            <Field
              label={t('regie:coffre.nouveauDocument.typeChamp')}
              htmlFor="nouveau-document-type"
            >
              <Select
                id="nouveau-document-type"
                aria-label={t('regie:coffre.nouveauDocument.typeChamp')}
                value={type}
                onValueChange={(valeur) => setType(valeur as TypeDocument)}
                options={TYPES_DOCUMENT.map((code) => ({
                  value: code,
                  label: t(`coffre:types.${code}`),
                }))}
              />
            </Field>

            <Field
              label={t('regie:coffre.nouveauDocument.confidentialiteChamp')}
              htmlFor="nouveau-document-confidentialite"
            >
              <Select
                id="nouveau-document-confidentialite"
                aria-label={t('regie:coffre.nouveauDocument.confidentialiteChamp')}
                value={confidentialite}
                onValueChange={(valeur) => setConfidentialite(valeur as Confidentialite)}
                options={CONFIDENTIALITES.map((code) => ({
                  value: code,
                  label: t(`coffre:confidentialite.${code}`),
                }))}
              />
            </Field>
          </div>

          <Field
            label={t('regie:coffre.nouveauDocument.rattachementsChamp')}
            htmlFor="nouveau-document-rattachements"
          >
            <RechercheRattachement
              galerieSlug={galerieSlug}
              valeurs={rattachements}
              onChanger={setRattachements}
            />
          </Field>

          <div className={styles.grille2}>
            <Field
              label={t('regie:coffre.nouveauDocument.echeanceChamp')}
              htmlFor="nouveau-document-echeance"
            >
              <Input
                id="nouveau-document-echeance"
                type="date"
                mono
                value={echeanceLe}
                onChange={(event) => setEcheanceLe(event.target.value)}
              />
            </Field>

            <Field
              label={t('regie:coffre.nouveauDocument.rappelsChamp')}
              htmlFor="nouveau-document-rappels"
            >
              <Input
                id="nouveau-document-rappels"
                mono
                disabled={!echeanceLe}
                placeholder="30,7,1"
                value={rappelsJours}
                onChange={(event) => setRappelsJours(event.target.value)}
              />
            </Field>
          </div>

          <Field
            label={t('regie:coffre.nouveauDocument.etiquettesChamp')}
            htmlFor="nouveau-document-etiquettes"
          >
            <Input
              id="nouveau-document-etiquettes"
              placeholder="bail, 2026"
              value={etiquettes}
              onChange={(event) => setEtiquettes(event.target.value)}
            />
            <p className={styles.aide}>{t('regie:coffre.nouveauDocument.etiquettesAide')}</p>
          </Field>

          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}
